// Météo d'une carte, réglée dans l'éditeur de cartes : un calque visuel en jeu (pluie, neige, brouillard) et,
// au choix, un type d'attaque renforcé dans les combats qui s'y déroulent. Elle ne dure qu'une part de la journée
// (voir `chance`), à des heures tirées au sort chaque jour. Partagé par le serveur, le jeu et l'administration.
import type { Element } from './elements.ts';
import type { DayNightConfig } from './lighting.ts';
import type { SpriteRef } from './map-elements.ts';

export const WEATHER_KINDS = { none: 'Aucune', rain: 'Pluie', snow: 'Neige', fog: 'Brouillard' } as const;
export type WeatherKind = keyof typeof WEATHER_KINDS;
export const isWeatherKind = (value: unknown): value is WeatherKind => typeof value === 'string' && Object.hasOwn(WEATHER_KINDS, value);

/**
 * Mouvement des images choisies pour la météo (voir Weather.particles). Chaque particule s'écarte des valeurs moyennes
 * (vitesse, rotation, retournement) selon `variation`, tourne dans un sens ou dans l'autre et se balance à son rythme.
 */
export interface ParticleMotion {
  /** Vitesse de chute, en cases par seconde. */
  speed: number;
  /** Direction de la chute, en degrés : 0 tout droit vers le bas, négatif vers la gauche, positif vers la droite. */
  direction: number;
  /** Écart de vitesse, de rotation et de retournement d'une particule à l'autre (0 : toutes pareilles, 1 : de 50 % à 150 %). */
  variation: number;
  /** Balancement de côté, en cases. */
  sway: number;
  /** Oscillation de l'image autour de son centre, en degrés (de part et d'autre). */
  wobble: number;
  /** Rotation continue, en tours par seconde. */
  spin: number;
  /** Retournements (l'image s'amincit puis montre son autre face), par seconde. */
  tumble: number;
  /** L'image est tournée dans le sens de la chute (une goutte en biais). */
  align: boolean;
  /** Taille des images, en cases. */
  size: number;
  /** Nombre de particules sur un écran de 1000 × 600 px, à intensité maximale. */
  density: number;
}

/** Nom, bornes, pas et unité des réglages chiffrés du mouvement (les curseurs de l'éditeur). */
export const PARTICLE_MOTION_LIMITS = {
  speed: { label: 'Vitesse', min: 0, max: 40, step: 0.1, unit: 'cases/s' },
  direction: { label: 'Direction', min: -90, max: 90, step: 1, unit: '°' },
  variation: { label: 'Écart entre particules', min: 0, max: 1, step: 0.05, unit: '' },
  sway: { label: 'Balancement', min: 0, max: 3, step: 0.05, unit: 'cases' },
  wobble: { label: 'Oscillation', min: 0, max: 180, step: 1, unit: '°' },
  spin: { label: 'Rotation', min: 0, max: 3, step: 0.02, unit: 'tours/s' },
  tumble: { label: 'Retournement', min: 0, max: 3, step: 0.02, unit: '/s' },
  size: { label: 'Taille', min: 0.25, max: 3, step: 0.05, unit: 'cases' },
  density: { label: 'Densité', min: 5, max: 400, step: 5, unit: '' },
} as const satisfies Record<Exclude<keyof ParticleMotion, 'align'>, { label: string; min: number; max: number; step: number; unit: string }>;
export type ParticleMotionNumber = keyof typeof PARTICLE_MOTION_LIMITS;

/** Mouvements tout faits, point de départ des réglages. */
export const PARTICLE_PRESETS = {
  rain: { label: 'Pluie (rapide, en biais)', motion: { speed: 24, direction: -14, variation: 0.4, sway: 0, wobble: 0, spin: 0, tumble: 0, align: true, size: 0.9, density: 140 } },
  snow: { label: 'Neige (lente, se balance)', motion: { speed: 1.6, direction: 0, variation: 0.8, sway: 0.55, wobble: 14, spin: 0, tumble: 0, align: false, size: 0.9, density: 110 } },
  petals: { label: 'Pétales (tournoient au vent)', motion: { speed: 2.4, direction: 40, variation: 0.8, sway: 0.95, wobble: 0, spin: 0.24, tumble: 0.32, align: false, size: 0.9, density: 70 } },
} as const satisfies Record<string, { label: string; motion: ParticleMotion }>;
export type ParticlePreset = keyof typeof PARTICLE_PRESETS;

/** Le préréglage dont le mouvement a exactement ces valeurs (null : mouvement personnalisé). */
export function presetOf(motion: ParticleMotion): ParticlePreset | null {
  const keys = Object.keys(motion) as (keyof ParticleMotion)[];
  const found = Object.entries(PARTICLE_PRESETS).find(([, preset]) => keys.every((key) => preset.motion[key] === motion[key]));
  return found ? found[0] as ParticlePreset : null;
}

/** Côté des images de la météo, en pixels du tileset (une case de la grille des tilesets de carte). */
export const PARTICLE_SIZE = 16;
/** Nombre maximal d'images différentes pour une météo. */
export const MAX_PARTICLES = 32;

export interface Weather {
  kind: WeatherKind;
  /** Force de la pluie, de la neige ou du brouillard (0 à 1). */
  intensity: number;
  /**
   * Part de la journée où il fait ce temps (0 à 1) : à 1, tout le temps ; à 0,5, 12 h sur 24, en une à trois averses
   * placées au hasard chaque jour (voir weatherStrength).
   */
  chance: number;
  /** Type dont les attaques sont renforcées (×WEATHER_BOOST) dans les combats de la carte ; null : aucun. */
  boostedElement: Element | null;
  /**
   * Images (cases de PARTICLE_SIZE px des tilesets de carte) qui tombent à la place des gouttes, des flocons ou des
   * nappes de brume, chacune tirée au hasard ; vide : le dessin par défaut de la météo.
   */
  particles: SpriteRef[];
  /** Mouvement des images choisies (sans effet sur le dessin par défaut). */
  motion: ParticleMotion;
  /** Voile coloré qui assombrit l'écran pendant la météo. */
  overcast: boolean;
}

export const NO_WEATHER: Weather = { kind: 'none', intensity: 0.5, chance: 1, boostedElement: null, particles: [], motion: PARTICLE_PRESETS.petals.motion, overcast: true };

/** Durée du fondu au début et à la fin d'une averse, en part de journée (une demi-heure du jeu). */
const FADE = 0.5 / 24;

/** Tirage pseudo-aléatoire (mulberry32) : le même pour une graine donnée, chez le serveur comme chez chaque joueur. */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Coupe `total` en `count` morceaux de tailles tirées au sort. */
function split(total: number, count: number, random: () => number): number[] {
  const weights = Array.from({ length: count }, () => 0.3 + random());
  const sum = weights.reduce((a, b) => a + b, 0);
  return weights.map((weight) => (total * weight) / sum);
}

/**
 * Force de la météo de la carte `mapId` à l'instant `now` (0 : il ne fait pas ce temps, 1 : en plein dedans).
 * Chaque journée du jeu (voir DayNightConfig) a ses averses, tirées au sort d'après la carte et le jour : une à trois,
 * qui durent en tout `chance` de la journée, séparées par des éclaircies et décalées d'une heure quelconque. Chacune
 * monte et retombe en fondu.
 */
export function weatherStrength(weather: Pick<Weather, 'kind' | 'chance'>, mapId: number, dayNight: Pick<DayNightConfig, 'cycleMinutes'>, now: number): number {
  if (weather.kind === 'none' || weather.chance <= 0) return 0;
  if (weather.chance >= 1) return 1;
  const cycle = dayNight.cycleMinutes * 60_000;
  const day = Math.floor(now / cycle);
  const random = seeded(Math.imul(mapId, 0x9e3779b1) ^ Math.imul(day, 0x85ebca6b));
  const count = 1 + Math.floor(random() * 3);
  const spells = split(weather.chance, count, random);
  const clears = split(1 - weather.chance, count, random);
  // Heure du jour, depuis le début de la première averse.
  let t = (((now % cycle) / cycle - random()) % 1 + 1) % 1;
  for (let i = 0; i < count; i++) {
    const spell = spells[i]!;
    if (t < spell) {
      const fade = Math.min(FADE, spell / 2);
      return Math.min(1, t / fade, (spell - t) / fade);
    }
    t -= spell + clears[i]!;
    if (t < 0) return 0;
  }
  return 0;
}

/**
 * Météo de la carte à l'instant `now` : celle de la carte pendant une averse (intensité en fondu), aucune entre
 * deux. Le type renforcé ne l'est que pendant l'averse, sauf sur une carte sans météo, où il l'est toujours.
 */
export function weatherAt(weather: Weather, mapId: number, dayNight: Pick<DayNightConfig, 'cycleMinutes'>, now: number): Weather {
  if (weather.kind === 'none') return weather;
  const strength = weatherStrength(weather, mapId, dayNight, now);
  if (strength <= 0) return { ...NO_WEATHER, chance: weather.chance };
  return { ...weather, intensity: weather.intensity * strength };
}

/** Multiplicateur des dégâts des attaques du type renforcé par la météo. */
export const WEATHER_BOOST = 1.25;
